export interface StoredAuth {
  user: import('./api').User
  accessToken: string
  refreshToken: string
  /** Absolute epoch milliseconds. */
  expires: number
  refreshExpires: number
}

// Key used by the test UI to remember its token.
const STORAGE_KEY = 'hcpexploreruser'

/**
 * Client-side holder for the access token.
 *
 * The server keeps no session, so the tokens live here and are presented on
 * each request. Worth being clear about the trade-off: localStorage is
 * readable by any script running on this origin, so a cross-site scripting bug
 * would expose the token. It is why shared files are served with a locked-down CSP and forced as
 * downloads — so an uploaded HTML or SVG file can never execute here.
 */
export const tokenStore = {
  read(): StoredAuth | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      return raw ? (JSON.parse(raw) as StoredAuth) : null
    } catch {
      return null
    }
  },

  write(auth: StoredAuth): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(auth))
    } catch {
      // Storage blocked; the session simply will not survive a reload.
    }
  },

  clear(): void {
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch {
      /* nothing to do */
    }
  },

  accessToken(): string | null {
    const auth = this.read()
    return auth?.accessToken ?? null
  },

  /** True when the access token is within five minutes of expiring. */
  needsRefresh(): boolean {
    const auth = this.read()
    if (!auth) return false
    return auth.expires - Date.now() < 5 * 60 * 1000
  },

  /** True when even the refresh token has expired — a full sign-in is needed. */
  isDead(): boolean {
    const auth = this.read()
    if (!auth) return true
    return auth.refreshExpires <= Date.now()
  },
}
